import { Fragment, useEffect, useMemo, useState } from 'react';
import { Alert } from '@cluey/cluey-components';
import { orderBy } from 'lodash';

import { api } from '../../../../api';
import { Loader } from '../../../../components/Loader';
import HubCompletedSessionsListItem from '../../../../components/hub/sessions/HubCompletedSessionsListItem';
import RecordsPagination from '../../../../components/hub/RecordsPagination';
import { getSessionsInViewByIndex, scrollToTop } from '../../../../util/helpers';
import { NO_OF_SESSIONS_PER_PAGE } from '../../../../util/constants';
import useSessionContext from '../useSessionContext';
import ErrorBlock from '../../../../common/ErrorBlock';

const CompletedSessions = () => {
  const {
    filteredSessions: { completed: completedSessions = [] },
    fetchSessionIsLoading: completedSessionsLoading,
    fetchSessionIsError: completedSessionsError,
  } = useSessionContext();
  const { data: accountDetails, isLoading: loadingAccountDetails } = api.account.details.useQuery();

  const [{ sessionsInView, totalSessions, startIndex }, setSessionDetails] = useState({
    allSessions: [],
    totalSessions: 0,
    sessionsInView: [],
    startIndex: 0,
  });
  const timezone = accountDetails?.timezone ?? 'Australia/Sydney';
  const isLoading = completedSessionsLoading || loadingAccountDetails;

  const sortedSessions = useMemo(() => {
    if (!completedSessions?.length) return [];
    return orderBy(completedSessions, 'startTime', 'desc');
  }, [completedSessions]);

  useEffect(() => {
    if (sortedSessions.length > 0) {
      const sessionsInView = getSessionsInViewByIndex(sortedSessions, startIndex);
      setSessionDetails((current) => {
        if (
          current.totalSessions === sortedSessions.length &&
          JSON.stringify(current.sessionsInView) === JSON.stringify(sessionsInView)
        ) {
          return current;
        }
        return {
          allSessions: sortedSessions,
          totalSessions: sortedSessions.length,
          startIndex,
          sessionsInView,
        };
      });
    } else {
      setSessionDetails((current) => {
        if (current.totalSessions === 0 && current.sessionsInView.length === 0) {
          return current;
        }
        return {
          allSessions: [],
          totalSessions: 0,
          sessionsInView: [],
          startIndex: 0,
        };
      });
    }
  }, [sortedSessions, startIndex]);

  const handlePaginationClick = (newStartIndex) => {
    setSessionDetails((current) => {
      return {
        ...current,
        sessionsInView: getSessionsInViewByIndex(current.allSessions, newStartIndex),
        startIndex: newStartIndex,
      };
    });
    scrollToTop();
  };

  return (
    <Fragment>
      {isLoading ? (
        <Loader />
      ) : (
        <Fragment>
          {completedSessionsError && (
            <Alert type="error" body="There was an error retrieving your completed sessions" />
          )}
          {!completedSessionsError &&
            (sortedSessions?.length === 0 ? (
              <ErrorBlock errorMsg="You have no completed sessions." />
            ) : (
              <Fragment>
                <div className="flex flex-col gap-4">
                  {sessionsInView.map((s) => (
                    <HubCompletedSessionsListItem
                      key={s.uniqueEnrolmentId + s.startTime}
                      session={s}
                      timezone={timezone}
                    />
                  ))}
                </div>
                <RecordsPagination
                  recordsTotal={totalSessions}
                  noOfRecordsPerPage={NO_OF_SESSIONS_PER_PAGE}
                  handlePaginationClick={handlePaginationClick}
                  pageIndex={startIndex}
                />
              </Fragment>
            ))}
        </Fragment>
      )}
    </Fragment>
  );
};

export default CompletedSessions;
